import { ReactNode } from "react";
import dayjs from "dayjs";
import {
  refundedAmountOf,
  SaleCustomer,
  SaleOutcome,
  SaleRecord,
} from "./SaleRecord.ts";
import {
  CustomerOrders,
  couponDiscountOfCustomerInCents,
  fullNameOf,
  numberOfOrdersOf,
  refundedToCustomerInCents,
  soldToCustomerInCents,
} from "./salesCustomers.ts";
import { itemKeyOf, paymentMethodKeyOf } from "./salesFilters.ts";
import {
  declineReasonLabelOf,
  paymentMethodLabelOf,
  SALE_OUTCOME_LABELS,
  SALE_OUTCOME_OF_ONE_ORDER_LABELS,
  SALES_GATEWAY_LABELS,
} from "./salesLabels.ts";
import { centsForCsv, CsvColumn, dateTimeForCsv } from "./salesCsv.ts";
import { formatCount, formatDay, formatEuros } from "./salesFormat.ts";
import { OutcomeOfOrder } from "./OutcomeOfOrder.tsx";
import { SalesColumnFilter } from "./salesColumnFilters.ts";

export type SalesDetailsColumn<Row> = CsvColumn<Row> & {
  cellOf?: (row: Row) => ReactNode;
  isNumeric?: boolean;
  onlyIn?: "table" | "csv";
  filter?: SalesColumnFilter<Row>;
};

export type OrderColumn = SalesDetailsColumn<SaleRecord>;
export type CustomerColumn = SalesDetailsColumn<CustomerOrders>;

function amountColumn<Row>(
  header: string,
  amountInCentsOf: (row: Row) => number,
): SalesDetailsColumn<Row> {
  return {
    header,
    valueOf: (row) => centsForCsv(amountInCentsOf(row)),
    cellOf: (row) => formatEuros(amountInCentsOf(row)),
    isNumeric: true,
    filter: { kind: "amountRange", amountInCentsOf },
  };
}

function countColumn<Row>(
  header: string,
  countOf: (row: Row) => number,
): SalesDetailsColumn<Row> {
  return {
    header,
    valueOf: countOf,
    cellOf: (row) => formatCount(countOf(row)),
    isNumeric: true,
    filter: { kind: "countRange", countOf },
  };
}

function dateColumn<Row>(
  header: string,
  utcDateTimeOf: (row: Row) => string | null,
): SalesDetailsColumn<Row> {
  return {
    header,
    valueOf: (row) => {
      const utcDateTime = utcDateTimeOf(row);
      return utcDateTime ? dateTimeForCsv(utcDateTime) : null;
    },
    cellOf: (row) => {
      const utcDateTime = utcDateTimeOf(row);
      return utcDateTime ? (
        <span className="whitespace-nowrap">
          {formatDay(dayjs(utcDateTime))}
        </span>
      ) : null;
    },
    filter: { kind: "dateRange", utcDateTimeOf },
  };
}

function distinctTextsOf(texts: (string | null)[]): string[] {
  return [...new Set(texts.filter((text): text is string => text !== null))];
}

function textColumn<Row>(
  header: string,
  textsOf: (row: Row) => (string | null)[],
): SalesDetailsColumn<Row> {
  return {
    header,
    valueOf: (row) => distinctTextsOf(textsOf(row)).join(", "),
    filter: {
      kind: "values",
      valuesOf: (row) => distinctTextsOf(textsOf(row)),
    },
  };
}

function couponCodesColumn<Row>(
  header: string,
  couponCodesOf: (row: Row) => (string | null)[],
): SalesDetailsColumn<Row> {
  const column = textColumn(header, couponCodesOf);
  return {
    ...column,
    cellOf: (row) => (
      <span className="font-mono break-all">{column.valueOf(row)}</span>
    ),
  };
}

function customerColumns<Row>(
  customerOf: (row: Row) => SaleCustomer,
): SalesDetailsColumn<Row>[] {
  return [
    {
      header: "Cliente",
      valueOf: (row) => fullNameOf(customerOf(row)),
      onlyIn: "table",
    },
    {
      header: "Nome",
      valueOf: (row) => customerOf(row).firstName,
      onlyIn: "csv",
    },
    {
      header: "Cognome",
      valueOf: (row) => customerOf(row).lastName,
      onlyIn: "csv",
    },
    {
      header: "Email",
      valueOf: (row) => customerOf(row).email,
      cellOf: (row) => (
        <span className="break-all">{customerOf(row).email}</span>
      ),
    },
  ];
}

function lastRefundedAtOf(order: SaleRecord): string | null {
  const refundDates = order.refunds
    .map((refund) => refund.refundedAtUtc)
    .sort();
  return refundDates[refundDates.length - 1] ?? null;
}

function itemLabelOfOrder(
  order: SaleRecord,
  itemLabelOf: (itemKey: string) => string,
): string | null {
  const itemKey = itemKeyOf(order);
  return itemKey ? itemLabelOf(itemKey) : null;
}

export function csvColumnsOf<Row>(
  columns: SalesDetailsColumn<Row>[],
): CsvColumn<Row>[] {
  return columns.filter((column) => column.onlyIn !== "table");
}

export function tableColumnsOf<Row>(
  columns: SalesDetailsColumn<Row>[],
): SalesDetailsColumn<Row>[] {
  return columns.filter((column) => column.onlyIn !== "csv");
}

export const ORDER_ID_COLUMN: OrderColumn = {
  header: "ID ordine",
  valueOf: (order) => order.orderId,
  onlyIn: "csv",
};

export const ORDER_DATE_COLUMN = dateColumn<SaleRecord>(
  "Data ordine",
  (order) => order.orderedAtUtc,
);

export const ORDER_CUSTOMER_COLUMNS = customerColumns<SaleRecord>(
  (order) => order.customer,
);

export function orderItemColumn(
  itemLabelOf: (itemKey: string) => string,
): OrderColumn {
  return textColumn("Prodotto o bundle", (order) => [
    itemLabelOfOrder(order, itemLabelOf),
  ]);
}

export const ORDER_GATEWAY_COLUMN = textColumn<SaleRecord>(
  "Gateway",
  (order) => [SALES_GATEWAY_LABELS[order.gateway]],
);

export const ORDER_PAYMENT_METHOD_COLUMN: OrderColumn = {
  header: "Metodo di pagamento",
  valueOf: (order) => paymentMethodLabelOf(paymentMethodKeyOf(order)),
  onlyIn: "csv",
};

export const ORDER_OUTCOME_COLUMN: OrderColumn = {
  ...textColumn<SaleRecord>("Esito", (order) => [
    SALE_OUTCOME_OF_ONE_ORDER_LABELS[order.outcome],
  ]),
  cellOf: (order) => <OutcomeOfOrder outcome={order.outcome} />,
};

export const ORDER_DECLINE_REASON_COLUMN = textColumn<SaleRecord>(
  "Motivo del rifiuto",
  (order) => [
    order.outcome === SaleOutcome.Declined
      ? declineReasonLabelOf(order.gateway, order.declineReason)
      : null,
  ],
);

export const ORDER_AMOUNT_COLUMN = amountColumn<SaleRecord>(
  "Importo",
  (order) => order.amountDueInCents,
);

export const ORDER_COUPON_COLUMN = couponCodesColumn<SaleRecord>(
  "Coupon",
  (order) => [order.couponCode],
);

export const ORDER_COUPON_DISCOUNT_COLUMN = amountColumn<SaleRecord>(
  "Sconto",
  (order) => order.couponDiscountInCents,
);

export const ORDER_REFUNDED_COLUMN = amountColumn<SaleRecord>(
  "Rimborsato",
  refundedAmountOf,
);

export const ORDER_REFUND_DATE_COLUMN = dateColumn<SaleRecord>(
  "Data rimborso",
  lastRefundedAtOf,
);

export const CUSTOMER_IDENTITY_COLUMNS = customerColumns<CustomerOrders>(
  ({ customer }) => customer,
);

export const CUSTOMER_ORDER_COUNT_COLUMN = countColumn<CustomerOrders>(
  "Ordini",
  numberOfOrdersOf,
);

export function customerOutcomeCountColumns(
  outcomes: SaleOutcome[],
): CustomerColumn[] {
  return outcomes.map((outcome) =>
    countColumn<CustomerOrders>(
      SALE_OUTCOME_LABELS[outcome],
      ({ orders }) =>
        orders.filter((order) => order.outcome === outcome).length,
    ),
  );
}

export function customerItemsColumn(
  itemLabelOf: (itemKey: string) => string,
): CustomerColumn {
  return textColumn("Prodotti o bundle", ({ orders }) =>
    orders.map((order) => itemLabelOfOrder(order, itemLabelOf)),
  );
}

export const CUSTOMER_COUPONS_COLUMN = couponCodesColumn<CustomerOrders>(
  "Coupon usati",
  ({ orders }) => orders.map((order) => order.couponCode),
);

export const CUSTOMER_SOLD_COLUMN = amountColumn<CustomerOrders>(
  "Venduto",
  soldToCustomerInCents,
);

export const CUSTOMER_COUPON_DISCOUNT_COLUMN = amountColumn<CustomerOrders>(
  "Sconto",
  couponDiscountOfCustomerInCents,
);

export const CUSTOMER_REFUNDED_COLUMN = amountColumn<CustomerOrders>(
  "Rimborsato",
  refundedToCustomerInCents,
);

export const CUSTOMER_LAST_ORDER_DATE_COLUMN = dateColumn<CustomerOrders>(
  "Ultimo ordine",
  ({ orders }) => orders[0]?.orderedAtUtc ?? null,
);
